import dayjs from 'dayjs'

import { Link } from '@atoms'
import Badge from '@atoms/Badge'
import EmptyState from '@components/molecules/EmptyState'
import { Table } from '@components/molecules/Table'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('supporters_export.history')

export type ExportRequester = {
  name: string | null
  isCurrentUser: boolean
}

export type ExportRecord = {
  id: number
  status: 'pending' | 'ready' | 'failed'
  requestedBy: ExportRequester
  supporterCount: number | null
  createdAt: string
  lastDownloadedAt: string | null
  downloadPath: string | null
}

const STATUS_COLOURS = {
  pending: 'warning',
  ready: 'success',
  failed: 'error',
} as const

const formatTimestamp = (iso: string) => dayjs(iso).format('D MMM YYYY, h:mm a')

const requesterLabel = ({ name, isCurrentUser }: ExportRequester) => {
  if (isCurrentUser) return t('requested_by_you')
  return name ?? t('requested_by_unknown')
}

const DownloadCell = ({ record }: { record: ExportRecord }) => (
  <div className="tw:flex tw:min-h-9 tw:items-center">
    {record.status === 'failed' && <span className="tw:text-gray-cool-500">{t('failed_hint')}</span>}
    {record.downloadPath && (
      <Link
        href={record.downloadPath}
        rank="secondary"
        size="sm"
        leadingIcon="file-download-03"
        ariaLabel={t('download_label', { date: formatTimestamp(record.createdAt) })}
      >
        {t('download')}
      </Link>
    )}
  </div>
)

export function ExportHistoryTable({ exports }: { exports: ExportRecord[] }) {
  if (exports.length === 0) {
    return <EmptyState iconType="file-05" headline={t('empty.headline')} subtext={t('empty.subtext')} />
  }

  return (
    <div className="tw:rounded-md tw:border">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.Head className="tw:whitespace-nowrap">{t('columns.requested')}</Table.Head>
            <Table.Head className="tw:whitespace-nowrap">{t('columns.supporters')}</Table.Head>
            <Table.Head className="tw:whitespace-nowrap">{t('columns.requested_by')}</Table.Head>
            <Table.Head className="tw:whitespace-nowrap">{t('columns.status')}</Table.Head>
            <Table.Head className="tw:whitespace-nowrap">{t('columns.last_downloaded')}</Table.Head>
            <Table.Head className="tw:whitespace-nowrap">{t('columns.download')}</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {exports.map((record) => (
            <Table.Row key={record.id}>
              <Table.Cell className="tw:whitespace-nowrap">{formatTimestamp(record.createdAt)}</Table.Cell>
              <Table.Cell className="tw:whitespace-nowrap tw:tabular-nums">
                {record.supporterCount === null ? (
                  <span className="tw:text-gray-cool-500">{t('supporters_unrecorded')}</span>
                ) : (
                  record.supporterCount.toLocaleString()
                )}
              </Table.Cell>
              <Table.Cell className="tw:whitespace-nowrap">{requesterLabel(record.requestedBy)}</Table.Cell>
              <Table.Cell className="tw:whitespace-nowrap">
                <Badge color={STATUS_COLOURS[record.status]}>{t(`status.${record.status}`)}</Badge>
              </Table.Cell>
              <Table.Cell className="tw:whitespace-nowrap">
                {record.lastDownloadedAt ? (
                  formatTimestamp(record.lastDownloadedAt)
                ) : (
                  <span className="tw:text-gray-cool-500">{t('never_downloaded')}</span>
                )}
              </Table.Cell>
              <Table.Cell className="tw:whitespace-nowrap">
                <DownloadCell record={record} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </div>
  )
}
